import { useCallback, useRef, useState } from 'react';
import type { Meeting } from '../../types';
import type { AudioImportFile } from '../../global';
import { t } from '../../lib/i18n';
import { buildRecordingDraft } from '../recording/buildRecordingDraft';
import { formatTimestamp } from '../recording/useRecorder';

/** preparing: dosya seçiliyor/dönüştürülüyor · transcribing: sunucuda döküm · summarizing: özet. */
export type UploadPhase = 'preparing' | 'transcribing' | 'summarizing';

export type UploadOutcome =
  | { kind: 'cancelled' }
  | { kind: 'draft'; draft: Omit<Meeting, 'id'>; filePath: string }
  | { kind: 'error'; message: string };

export function uploadPhaseLabel(phase: UploadPhase): string {
  if (phase === 'preparing') return t('Dosya hazırlanıyor…', 'Preparing file…');
  if (phase === 'transcribing') return t('Yazıya dökülüyor…', 'Transcribing…');
  return t('Özetleniyor…', 'Summarizing…');
}

/**
 * Ana ekrandaki "Ses dosyası yükle": dosyayı ana süreç seçtirip denetliyor ve
 * temiz bir mp3'e çeviriyor (audioImport.ts), sunucu yazıya döküyor, özet ve
 * not taslağı kayıt sonrasıyla aynı yoldan çıkıyor. Kalıcılık çağıranın işi.
 */
export function useAudioUpload() {
  const [phase, setPhase] = useState<UploadPhase | null>(null);
  const busy = useRef(false);

  /**
   * `preset` verilirse dosya seçtirilmez — web'de yarıda kalıp kurtarılan
   * kayıtlar bu yoldan yazıya dökülüyor.
   */
  const run = useCallback(async (preset?: AudioImportFile): Promise<UploadOutcome> => {
    if (busy.current) return { kind: 'cancelled' };
    busy.current = true;
    setPhase('preparing');
    let filePath: string | null = null;
    try {
      const file = preset ?? (await window.zabit.pickAudioFile());
      if (!file) return { kind: 'cancelled' };
      filePath = file.filePath;

      setPhase('transcribing');
      const result = await window.zabit.transcribeAudioFile(file.filePath, file.liveWindow ? { liveWindow: file.liveWindow } : undefined);
      const durationSeconds = result.durationSeconds || file.durationSeconds;
      const baseName = file.fileName.replace(/\.[^.]+$/, '').trim().slice(0, 120) || t('Yüklenen kayıt', 'Uploaded recording');

      const draft = await buildRecordingDraft({
        transcript: result.transcript,
        durationSeconds,
        filePath: file.filePath,
        // Başlığı yapay zekâ verir; veremezse dosyanın adı.
        userTitle: '',
        fallbackTitle: baseName,
        recordedAt: new Date(file.recordedAtISO),
        space: 'private',
        participants: [],
        tags: [t('Yükleme', 'Upload')],
        callout: ({ speakers, hasTranscript }) =>
          t(
            `📁 "${file.fileName}" dosyasından yüklendi · ${formatTimestamp(durationSeconds)}.${
              hasTranscript ? ` Konuşmacılar: ${speakers.join(', ')}.` : ''
            }`,
            `📁 Uploaded from "${file.fileName}" · ${formatTimestamp(durationSeconds)}.${
              hasTranscript ? ` Speakers: ${speakers.join(', ')}.` : ''
            }`
          ),
        onSummarizing: () => setPhase('summarizing'),
      });
      return { kind: 'draft', draft, filePath: file.filePath };
    } catch (err) {
      // Yarıda kalan yüklemenin dönüştürülmüş dosyası diskte kalmasın.
      if (filePath) await window.zabit.discardAudioFile(filePath).catch(() => {});
      return {
        kind: 'error',
        message: (err as Error).message || t('Dosya yüklenemedi.', 'Could not upload the file.'),
      };
    } finally {
      busy.current = false;
      setPhase(null);
    }
  }, []);

  return { phase, run };
}
